import {
  Box,
  Divider,
  ErrorfilledIcon,
  GKButton,
  GKTextField,
  Grid2,
  Typography,
  UploadIcon,
  VerifyLineIcon,
} from "components";
import GKImageUploader from "components/GKImageUploader";
import { isEmpty } from "lodash";
import { useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  useConfirmDomainMutation,
  useUpdateMyWebsiteDetailsMutation,
} from "store/features/MyWebsite";
import { setCroppedImage, setSelectedImage } from "store/slices/MyWebsiteSlice";
import { validateNotWhitespaceOnly } from "utils/validationSchema";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { WebsiteDetails } from "./types";

const PRESET_COLORS = [
  "#006580",
  "#E47354",
  "#F5982A",
  "#339C8F",
  "#588157",
  "#403D39",
  "#222222",
];

const MyBrandingForm = () => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const form = useFormContext<WebsiteDetails>();

  const dispatch = useAppDispatch();

  const id = useAppSelector((state) => state?.user?.userData?.companyId);
  const { selectedImage, croppedImage, websiteDetails } = useAppSelector(
    (state) => state.myWebsite,
  );

  const [updateMyWebsiteDetails] = useUpdateMyWebsiteDetailsMutation();
  const [confirmDomain, { isLoading }] = useConfirmDomainMutation();

  const handleSetSelectedImage = (img: string | null | undefined) => {
    dispatch(setSelectedImage(img));
  };

  const handleSetCroppedImage = (img: string | null | undefined) => {
    dispatch(setCroppedImage(img));
  };

  const handleVerifyDomain = async () => {
    try {
      const response = await confirmDomain({
        data: form.getValues("websiteSection.myBranding.domainName"),
      }).unwrap();

      if (response?.available) {
        form.clearErrors("websiteSection.myBranding.domainName");
        form.setValue("websiteSection.myBranding.isVerified", true);
      }
    } catch (err: unknown | any) {
      form.setError("websiteSection.myBranding.domainName", {
        type: "manual",
        message:
          "Domain verification failed. Please check your domain name and try again.",
      });
      console.error("Domain verification failed:", err);
    }
  };

  const handleDeleteImage = async () => {
    if (isEmpty(form.getValues("websiteSection.myBranding.companyLogo"))) {
      dispatch(setSelectedImage(null));
      dispatch(setCroppedImage(null));
      return;
    }

    try {
      await updateMyWebsiteDetails({
        id,
        body: new FormData(),
        path: "my_branding",
        isDelete: true,
        field: "companyLogo",
      }).unwrap();
    } catch (err) {
      console.error("Update my website details failed:", err);
    }
  };

  return (
    <Grid2 container spacing={3}>
      <Grid2 container size={12} spacing={1}>
        <Grid2 size={10}>
          <Controller
            name="websiteSection.myBranding.domainName"
            control={form.control}
            rules={{
              required: {
                value: true,
                message: "Please enter a valid domain name",
              },
              validate: validateNotWhitespaceOnly,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <GKTextField
                  {...field}
                  inputLabel="Domain Name"
                  requiredField
                  sx={{ borderColor: "green" }}
                  placeholder="Your domain name (e.g. grewup)"
                  onChange={(event) => {
                    field.onChange(
                      event.target.value.toLowerCase().replace(/\s/g, ""),
                    );
                  }}
                  slotProps={{
                    input: {
                      endAdornment: form.getValues(
                        "websiteSection.myBranding.isVerified",
                      ) ? (
                        <VerifyLineIcon />
                      ) : (
                        <ErrorfilledIcon />
                      ),
                    },
                  }}
                  error={!!error}
                  helperText={error?.message}
                />
              );
            }}
          />
        </Grid2>
        <Grid2 size={2} display={"flex"} alignItems={"center"}>
          <GKButton
            loading={isLoading}
            variant="text"
            disabled={
              websiteDetails?.websiteSection?.myBranding?.domainName ===
              form.watch("websiteSection.myBranding.domainName")
            }
            onClick={handleVerifyDomain}
          >
            Verify
          </GKButton>
        </Grid2>

        <Grid2 size={12}>
          <Typography fontSize={12} fontWeight={500} color="var(--grey-500)">
            .gridkey.in
          </Typography>
        </Grid2>
      </Grid2>
      <Grid2 size={12}>
        <Divider />
      </Grid2>
      <Grid2 container size={12} spacing={2}>
        <Grid2 size={12}>
          <Typography fontSize={13} fontWeight={500} color="var(--grey-900)">
            Upload Your Firm Logo:
          </Typography>
        </Grid2>
        <Grid2 size={12}>
          <Controller
            name="websiteSection.myBranding.companyLogo"
            control={form.control}
            render={() => (
              <GKImageUploader
                croppedImage={croppedImage}
                handleProfilePicDelete={handleDeleteImage}
                selectedImage={selectedImage}
                setCroppedImage={handleSetCroppedImage}
                setSelectedImage={handleSetSelectedImage}
                form={form}
                logoName={"websiteSection.myBranding.companyLogo"}
                buttonName="Upload Image"
                startIcon={<UploadIcon />}
              />
            )}
          />
        </Grid2>
      </Grid2>
      <Grid2 size={12}>
        <Divider />
      </Grid2>
      <Grid2 container size={12} spacing={1}>
        <Grid2 size={12}>
          <Typography fontSize={13} fontWeight={500} color="var(--grey-900)">
            Selected Color:
          </Typography>
        </Grid2>
        <Grid2 size={12}>
          <Controller
            control={form.control}
            name="websiteSection.myBranding.colorPalette.primary"
            render={({ field }) => (
              <div
                style={{
                  background: field.value,
                  height: 48,
                  width: 48,
                  borderRadius: 8,
                }}
              />
            )}
          />
        </Grid2>
      </Grid2>

      <Grid2 size={12} container spacing={1}>
        <Grid2 size={12}>
          <Typography fontSize={13} fontWeight={500} color="var(--grey-900)">
            Choose Your Website Primary Color:
          </Typography>
        </Grid2>
        <Grid2 size={12} display="flex" alignItems="center" gap={0.5}>
          <Controller
            control={form.control}
            name="websiteSection.myBranding.colorPalette.primary"
            render={({ field }) => {
              const { value, onChange } = field;

              const openPicker = () => {
                const picker = inputRef.current;
                if (!picker) return;
                if (typeof picker.showPicker === "function") {
                  picker.showPicker();
                } else {
                  picker.click();
                }
              };

              return (
                <>
                  <Box display="flex" gap={0.5}>
                    {PRESET_COLORS.map((color) => {
                      const isSelected = value === color;
                      return (
                        <Box
                          key={color}
                          onClick={() => onChange(color)}
                          sx={{
                            height: 36,
                            width: 36,
                            borderRadius: "50%",
                            padding: "3px",
                            border: isSelected
                              ? `1px solid ${color}`
                              : "1px solid transparent",
                            boxSizing: "border-box",
                            cursor: "pointer",
                          }}
                        >
                          <Box
                            sx={{
                              backgroundColor: color,
                              borderRadius: "50%",
                              width: "100%",
                              height: "100%",
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>

                  <Box
                    display="inline-flex"
                    alignItems="center"
                    position="relative"
                    ml={0.5}
                  >
                    <Box
                      onClick={openPicker}
                      sx={{ cursor: "pointer", display: "inline-block" }}
                    >
                      <img
                        src="/colorCircle.svg"
                        alt="color picker"
                        style={{ height: 32, width: 32, display: "block" }}
                      />
                    </Box>

                    <input
                      ref={inputRef}
                      type="color"
                      value={value ?? "#006580"}
                      onChange={(e) => onChange(e.target.value)}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        height: 32,
                        width: 32,
                        opacity: 0,
                        cursor: "pointer",
                        border: "1px solid transparent",
                        padding: "3px",
                        boxSizing: "border-box",
                      }}
                    />
                  </Box>
                </>
              );
            }}
          />
        </Grid2>
      </Grid2>
    </Grid2>
  );
};

export default MyBrandingForm;
